import { 
  useContext, 
  useMemo, 
  useCallback, 
  useRef, 
  useState, 
  useEffect 
} from "react";

import { getProductPrediction, sumColumn } from "../../../../../../utils/utils";


import { BodyCells, CheckCell, HierarchyBodyCells } from "../../../../sharedComponents/analysisModuleSharedComponents.index"

import { Button, Text } from "@aidron/aidron-ds";

import { i18n } from "../../../../../../translate/i18n";

import PropTypes from 'prop-types';

import { useForecastStore } from "../../../../../../viewModel/stores/forecastStore";
import { TenantContext } from "../../../../../../templates/context/TenantContext";
import { DEFAULT_VALUES, ORDERING_MODES } from "../../../../../../components/components.constants";

import { useShallow } from "zustand/react/shallow";

const { t } = i18n;

const getAllPredictionsFromMonth = (month, predictionData) => {
  const prediction = predictionData.filter(
    prediction => prediction.year === month.year && prediction.month === month.month
  );
  return prediction !== -1 ? prediction : {};
};

const calculateParticipation = (values) => {
  const allPredictions = sumColumn(values.allPredictions, values.name);
  const selectedPredictions = sumColumn(values.selectedPredictions, values.name);
  return (selectedPredictions / allPredictions) * 100;
};

const calculateDiff = (values) => {
  const sumIndexOne = sumColumn(values.selectedPredictions, values.indexOne);
  const sumIndexTwo = sumColumn(values.selectedPredictions, values.indexTwo);
  return sumIndexOne - sumIndexTwo;
};

const calculateDiffPercent = (values) => {
  const sumIndexOne = sumColumn(values.selectedPredictions, values.indexOne);
  const sumIndexTwo = sumColumn(values.selectedPredictions, values.indexTwo);

  if (sumIndexOne && sumIndexTwo) return ((sumIndexOne - sumIndexTwo) / sumIndexTwo) * 100;
  
  return null;
}

const forecastColumns = () => {
  return [
    {
      label: t("partPercent"),
      name: "participationInAidron",
      fullLabel: t("percentParticipation"),
      type: "%",
      calculate: values => calculateParticipation(values),
    },
    {
      label: t("currentPlan"),
      name: "clientPrediction",
      type: "demand",
      calculate: values => sumColumn(values.selectedPredictions, values.name),
    },
    {
      label: t("ai"),
      name: "aidronPrediction",
      fullLabel: t("aiForecast"),
      type: "prediction",
      calculate: values => sumColumn(values.selectedPredictions, values.name),
    },
    {
      label: t("differenceAidronAndClient"),
      name: "differenceAidronAndClient",
      indexOne: "aidronPrediction",
      indexTwo: "clientPrediction",
      type: "dif",
      calculate: values => calculateDiff(values),
      colorReferenceFrom: "percentAidronVsClient",
    },
    {
      label: t("percentAidronVsClient"),
      name: "percentAidronVsClient",
      indexOne: "aidronPrediction",
      indexTwo: "clientPrediction",
      type: "dif%",
      calculate: values => calculateDiffPercent(values),
    },
  ];
};

const calculatePrediction = (month, predictions, selectedProductIds) => {
  const allPredictionsFromMonth = getAllPredictionsFromMonth(month, predictions);

  const monthProductPredictions = allPredictionsFromMonth.filter(
    prediction => selectedProductIds.includes(prediction.productId)
  );

  const result = {};
  forecastColumns().forEach(column => {
    const values = { 
      allPredictions: allPredictionsFromMonth, 
      selectedPredictions: monthProductPredictions, 
      ...column 
    };
    result[column.name] = column.calculate(values);
  });

  return result;
};

const historicColumns = ({ unit }) => {
  return [
    {
      label: t("realized"),
      name: "realized",
      fullLabel: t("realizedSales"),
      type: "realized",
    },
    {
      label: t("partPercent"),
      name: "participationInRealized",
      fullLabel: t("percentParticipationInRealized"),
      type: "participationInRealized",
    }
  ];
};

function* createProductIdsGenerator(productIds) {
  for (const productId of productIds) {
    yield productId;
  }
}

export function PredictionRows({ actions, ctx, uiState }) {

  const {
    setCheckColumnWidth,
    setHierarchyColumnsWidth,
    setCheckedProducts
  } = actions || {}

  const { 
    selectedProductIds,
    checkedProducts,
    selectedHierarchyIndexes,
    products,
    historicPredictionsToUse
  } = ctx || {};

  const { 
    freezeColumns,
    sortRowsBy,
    sumPreviousColumnsWidth,
    evaluateLastCategoryColumn,
    headerHeight,
    defineHierarchyToDisplay
  } = uiState || {};

  const { unit } = useContext(TenantContext);

  const { latestForecast, } = useForecastStore(
    useShallow((state) => ({
      latestForecast: state.latestForecast,
    }))
  );

  const { predictions = [], monthColumns } = latestForecast || {};

  const productGeneratorRef = useRef(null);
  const [visibleProducts, setVisibleProducts] = useState([]);
  
  const defineLeftCellBorder = useCallback((monthIndex, columnIndex) => {
    return columnIndex === 0 && (historicPredictionsToUse.length !== 0 || monthIndex !== 0);
  }, [historicPredictionsToUse]);

  const forecastColumnsBorders = useMemo(() => {
    return monthColumns.map((month, index) => forecastColumns().map((_, i) => defineLeftCellBorder(index, i)));
  }, [monthColumns, defineLeftCellBorder]);

  const uniqueHistoricMonths = useMemo(() => {
    const seen = new Set();
    return [...(historicPredictionsToUse || [])]
      .sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month)
      .filter(h => {
        const key = `${h.year}-${h.month}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map(h => ({ year: h.year, month: h.month }));
  }, [historicPredictionsToUse]);

  const orderedProductIds = useMemo(() => {
    const sorted = [...selectedProductIds];

    if (!sortRowsBy?.columnLabel) return sorted;

    const isHistoric = ["realized", "participationInRealized"].includes(sortRowsBy.columnLabel);

    const getPredictionValue = (productId) => {
      if (isHistoric) {
        const entry = historicPredictionsToUse.find(
          h => h.productId === productId &&
              h.month === sortRowsBy.months[0] &&
              h.year === sortRowsBy.year
        );
        const val = entry?.[sortRowsBy.columnLabel];
        return val != null ? Number(val) : 0;
      }

      const month = monthColumns.find(
        m => m.month === sortRowsBy.months[0] && m.year === sortRowsBy.year
      );
      if (!month) return 0;

      const prediction = getProductPrediction(month, productId, predictions);
      return prediction?.[sortRowsBy.columnLabel] ?? 0;
    };

    sorted.sort((a, b) => {
      const aValue = getPredictionValue(a);
      const bValue = getPredictionValue(b);
      return sortRowsBy.order === ORDERING_MODES.ASCENDING
        ? aValue - bValue
        : bValue - aValue;
    });

    return sorted;
  }, [selectedProductIds, sortRowsBy, historicPredictionsToUse]);

  const loadMoreProducts = useCallback(() => {
    const nextBatch = [];
    for (let i = 0; i < 10; i++) {
      const { value, done } = productGeneratorRef.current.next();
      if (done) break;
      nextBatch.push(value);
    }
    if (nextBatch.length > 0) {
      setVisibleProducts(prevProducts => [...prevProducts, ...nextBatch]);
    }
  }, []);
  
  useEffect(() => {
    productGeneratorRef.current = createProductIdsGenerator(orderedProductIds);
    const initialBatch = [];
    for (let i = 0; i < 10; i++) {
      const { value, done } = productGeneratorRef.current.next();
      if (done) break;
      initialBatch.push(value);
    }
    setVisibleProducts(initialBatch);
  }, [orderedProductIds]);

  return (
    <>
      <PredictionRows.TotalRowForAllCells
        actions={{ setCheckedProducts: setCheckedProducts }}
        ctx={{
          checkedProducts: checkedProducts,
          selectedProductIds: selectedProductIds,
          latestForecast: latestForecast,
          historicPredictionsToUse: historicPredictionsToUse,
          uniqueHistoricMonths: uniqueHistoricMonths
        }}
        uiState={{
          freezeColumns: freezeColumns,
          headerHeight: headerHeight,
          defineHierarchyToDisplay: defineHierarchyToDisplay,
          sumPreviousColumnsWidth: sumPreviousColumnsWidth,
          evaluateLastCategoryColumn: evaluateLastCategoryColumn
        }}/>

      {visibleProducts.map((productId) => (
        <tr key={productId.productId}>
          <CheckCell
            actions={{
              setCheckColumnWidth: setCheckColumnWidth,
              setCheckedProducts: setCheckedProducts
            }}
            ctx={{
              productId: productId,
              checkedProducts: checkedProducts,
              selectedHierarchyIndexes: selectedHierarchyIndexes
            }}
            uiState={{
              individualProductCheck: true,
              freezeColumns: freezeColumns
            }}/>

          <HierarchyBodyCells
            ctx={{ 
              productId: productId, 
              selectedHierarchyIndexes: selectedHierarchyIndexes,
              allProducts: products
            }}
            uiState={{
              freezeColumns: freezeColumns,
              sumPreviousColumnsWidth: sumPreviousColumnsWidth,
              setHierarchyColumnsWidth: setHierarchyColumnsWidth,
              evaluateLastCategoryColumn: evaluateLastCategoryColumn
            }}/>

         {uniqueHistoricMonths.map((month, i) => {
            const productData = historicPredictionsToUse.find(
              h => h.productId === productId && h.year === month.year && h.month === month.month
            );
            return (
              <BodyCells
                ctx={{
                  individualProduct: true,
                  columns: historicColumns({ unit }),
                  productIds: selectedProductIds,
                  monthIndex: i,
                  prediction: productData
                    ? { ...productData, participationInRealized: Number(productData.participationInRealized) * 100 }
                    : { realized: null, participationInRealized: null }
                }}
                uiState={{ forecastColumnsBorders: [i !== 0, false] }}
                key={`${productId}-${month.year}-${month.month}`}
              />
            );
          })}
          {monthColumns.map((month, index) => (
            <BodyCells
              ctx={{
                monthIndex: index,
                individualProduct: true,
                productIds: selectedProductIds,
                columns: forecastColumns(),
                prediction: getProductPrediction(month, productId, predictions)
              }}
              uiState={{
                forecastColumnsBorders: forecastColumnsBorders[index]
              }}
              key={`${productId.productId}-${month.predictionId}`}
            />
          ))}
        </tr>
      ))}

      {visibleProducts.length < selectedProductIds.length && (
        <tr className="load-more-row">
          <td colSpan={defineHierarchyToDisplay().length} style={{
            textAlign: 'center',
            padding: '1rem',
            border: 'none',
            position: 'sticky',
            left: '2rem',
            bottom: '0',
            backgroundColor: 'white',
            zIndex: 1,
          }}>
            <Button
              variant={'primary'}
              onClick={loadMoreProducts}
              label={`${selectedProductIds.length - visibleProducts.length} restantes`}
              icon={'arrow-down'}
              position={'left'}/>
          </td>
        </tr>
      )}
    </>
  );
};

PredictionRows.TotalRowForAllCells = PredictionsSubtotalRow;


function PredictionsSubtotalRow({ actions, ctx, uiState }) {

  const { setCheckedProducts } = actions || {};

  const { 
    checkedProducts,
    selectedProductIds,
    latestForecast,
    historicPredictionsToUse,
    uniqueHistoricMonths
  } = ctx || {};

  const {
    monthHeaderCellHeight,
    defineHierarchyToDisplay,
    sumPreviousColumnsWidth,
    headerHeight,
    evaluateLastCategoryColumn,
    freezeColumns
  } = uiState || {}

  const { unit } = useContext(TenantContext);

  const { predictions = [], monthColumns = [] } = latestForecast;

  const defineLeftCellBorder = useCallback((monthIndex, columnIndex) => {
    return columnIndex === 0 && (historicPredictionsToUse.length !== 0 || monthIndex !== 0);
  }, [historicPredictionsToUse]);

  const forecastColumnsBorders = useMemo(() => {
    return monthColumns.map((month, index) =>
      forecastColumns().map((_, i) => defineLeftCellBorder(index, i))
    );
  }, [monthColumns, defineLeftCellBorder]);
  
  if (!selectedProductIds.length) return null;

  return (
    <tr className="font-weight-bold-on-paragraph">
      <CheckCell  
        actions={{ setCheckedProducts: setCheckedProducts }}
        ctx={{
          checkedProducts: checkedProducts,
          productIds: selectedProductIds
        }}
        uiState={{
          freezeColumns: freezeColumns,
          headerHeight: headerHeight
        }}/>

      {!!defineHierarchyToDisplay().length &&
        <td
          colSpan={defineHierarchyToDisplay().length}
          className={`background-color-white padding-sides-040rem z-2 cell-bottom-border position-sticky
          ${evaluateLastCategoryColumn(defineHierarchyToDisplay().length - 1)} ${freezeColumns ? "" : "cell-right-border"}
          `}
          style={{
            left: freezeColumns ? `${sumPreviousColumnsWidth(1)}px` : "", 
            top: `${headerHeight}px`,
          }}
        >
          <Text className="font-size-17px padd-text">{t("total")}</Text>
        </td>
      }

      {uniqueHistoricMonths.map((month, i) => {
        const realized = historicPredictionsToUse
          .filter(h => h.year === month.year && h.month === month.month)
          .reduce((sum, h) => sum + (h.realized || 0), 0);
        const allRealized = historicPredictionsToUse
          .filter(h => h.year === month.year && h.month === month.month)
          .reduce((sum, h) => sum + (h.realized || 0), 0);
        return (
          <BodyCells
            ctx={{
              columns: historicColumns({ unit }),
              productIds: selectedProductIds,
              monthIndex: i,
              prediction: {
                realized,
                participationInRealized: allRealized ? (realized / allRealized) * 100 : null
              }
            }}
            uiState={{
              headerHeight: headerHeight,
              forecastColumnsBorders: [i !== 0, false]
            }}
            key={`${month.year}-${month.month}`}
          />
        );
      })}

      {monthColumns.map((month, index) => (
        <BodyCells
          ctx={{
            prediction: calculatePrediction(month, predictions, selectedProductIds),
            columns: forecastColumns(),
            productIds: selectedProductIds,
            monthIndex: index
          }}
          uiState={{
            headerHeight: headerHeight,
            forecastColumnsBorders: forecastColumnsBorders[index]
          }}
          key={`month-${month.month}-predictionId-${month.predictionId}-product-${month.productId}`}
        />
      ))}
    </tr>
  )
}

PredictionsSubtotalRow.propTypes = { 
  actions: PropTypes.shape(),
  ctx: PropTypes.shape(),
  uiState: PropTypes.shape()
};

PredictionRows.propTypes = { 
  actions: PropTypes.shape(),
  ctx: PropTypes.shape(),
  uiState: PropTypes.shape()
};